import { BOOLEAN_OPERATIONS } from '@/adapters/primitive/_data/booleanOperations';
import type { BooleanAlgebraOperation, BooleanInputRole, BooleanPathAttempt } from '@/adapters/primitive/_contract/boolean/operation';

export function isBooleanAlgebraOperation(operation: string): operation is BooleanAlgebraOperation {
    return Object.prototype.hasOwnProperty.call(BOOLEAN_OPERATIONS, operation);
}

export function resolveBooleanAlgebra(args: {
    operation: BooleanAlgebraOperation;
    inputs: readonly [boolean] | readonly [boolean, boolean];
}): { value: boolean; pathKey: string | null; swapped: boolean } {
    const paths = BOOLEAN_OPERATIONS[args.operation].paths;
    for (const path of paths) {
        const required = path.requiredInputs;
        if (args.inputs.length !== required.length) continue;
        if (args.inputs.every((value, index) => value === required[index])) {
            return { value: true, pathKey: path.pathKey, swapped: false };
        }
        if (required.length === 2 && args.inputs[0] === required[1] && args.inputs[1] === required[0]) {
            return { value: true, pathKey: path.pathKey, swapped: true };
        }
    }
    return { value: false, pathKey: null, swapped: false };
}

export function planBooleanPathAttempts(args: {
    operation: BooleanAlgebraOperation;
    inputs: readonly [boolean] | readonly [boolean, boolean];
}): { attempts: readonly BooleanPathAttempt[]; value: boolean } {
    const attempts: BooleanPathAttempt[] = [];
    for (const path of BOOLEAN_OPERATIONS[args.operation].paths) {
        const required = path.requiredInputs;
        if (required.length !== args.inputs.length) continue;
        const first: BooleanInputRole = 'boolean-input-one';
        const second: BooleanInputRole = 'boolean-input-two';
        const roleNear: BooleanInputRole = required.length === 2
            && args.inputs[0] !== required[1] && args.inputs[1] === required[1] ? second : first;
        const roleFar: BooleanInputRole | null = required.length === 2
            ? roleNear === first ? second : first : null;
        const valueNear = args.inputs[roleNear === first ? 0 : 1];
        const fitsNear = valueNear === required[required.length - 1];
        const fitsFar = roleFar === null ? null : args.inputs[roleFar === first ? 0 : 1] === required[0];
        const firstMismatch = !fitsNear ? 'near' : fitsFar === false ? 'far' : null;
        attempts.push({ pathKey: path.pathKey, roleNear, roleFar, fitsNear, fitsFar, firstMismatch });
        if (firstMismatch === null) break;
    }
    return { attempts, value: attempts.at(-1)?.firstMismatch === null };
}